import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import {
  useCreateMirrorTargetMutation,
  useDeleteMirrorTargetMutation,
  useMirrorTargetsQuery,
  useUpdateMirrorTargetMutation,
  type MirrorTargetsQuery,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";
import MirrorTargetCompaniesEditor from "./mirror-target-companies-editor";

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  } satisfies SxProps<Theme>,
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxWidth: 640,
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    p: 2,
  } satisfies SxProps<Theme>,
};

type MirrorTargetRow = MirrorTargetsQuery["mirrorTargets"][number];

type TargetFormState = {
  uuid: string | null;
  name: string;
  url: string;
  enabled: boolean;
  batchSize: string;
  subscribesToAllCompanies: boolean;
};

const EMPTY_FORM: TargetFormState = {
  uuid: null,
  name: "",
  url: "",
  enabled: true,
  batchSize: "30",
  subscribesToAllCompanies: false,
};

/**
 * Production-side panel: the mirror target registry (one row per receiving
 * environment) with create/edit/delete and a per-company gating editor for
 * non-wildcard targets. The shared mirror secret is environment-variable
 * only and never appears here.
 */
const MirrorTargetsPanel = () => {
  const { data, loading, error, refetch } = useMirrorTargetsQuery({
    fetchPolicy: "network-only",
  });
  const [createMirrorTarget, { loading: creating }] =
    useCreateMirrorTargetMutation();
  const [updateMirrorTarget, { loading: updating }] =
    useUpdateMirrorTargetMutation();
  const [deleteMirrorTarget] = useDeleteMirrorTargetMutation();

  const [form, setForm] = useState<TargetFormState | null>(null);
  const [companiesTargetUuid, setCompaniesTargetUuid] = useState<string | null>(
    null,
  );
  const [mutationError, setMutationError] = useState<string | null>(null);

  const targets = useMemo(() => data?.mirrorTargets ?? [], [data]);

  const startEdit = useCallback((target: MirrorTargetRow) => {
    setMutationError(null);
    setForm({
      uuid: target.uuid,
      name: target.name,
      url: target.url,
      enabled: target.enabled,
      batchSize: String(target.batchSize),
      subscribesToAllCompanies: target.subscribesToAllCompanies,
    });
  }, []);

  const submitForm = useCallback(async () => {
    if (form === null) return;
    setMutationError(null);
    const shared = {
      name: form.name.trim(),
      url: form.url.trim(),
      enabled: form.enabled,
      batchSize: Number(form.batchSize) > 0 ? Number(form.batchSize) : 30,
      subscribesToAllCompanies: form.subscribesToAllCompanies,
    };
    try {
      const result = await (form.uuid === null
        ? createMirrorTarget({ variables: { input: shared } })
        : updateMirrorTarget({
            variables: { input: { uuid: form.uuid, ...shared } },
          }));
      // errorPolicy is 'all', so a backend rejection surfaces here, not as a
      // throw; without this the save fails silently and the form just closes.
      throwOnGraphqlErrors(result);
      setForm(null);
      await refetch();
    } catch (submitError) {
      setMutationError(
        submitError instanceof Error ? submitError.message : "Save failed",
      );
    }
  }, [createMirrorTarget, form, refetch, updateMirrorTarget]);

  const removeTarget = useCallback(
    async (uuid: string) => {
      setMutationError(null);
      try {
        const result = await deleteMirrorTarget({ variables: { uuid } });
        throwOnGraphqlErrors(result);
        await refetch();
      } catch (deleteError) {
        setMutationError(
          deleteError instanceof Error ? deleteError.message : "Delete failed",
        );
      }
    },
    [deleteMirrorTarget, refetch],
  );

  if (error !== undefined) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return (
    <Box sx={styles.panel}>
      <Typography variant="body1">
        Configure which environments receive mirrored production orders. The
        shared secret lives in environment variables, never here.
      </Typography>
      {mutationError !== null && (
        <Alert severity="error">{mutationError}</Alert>
      )}
      <Box>
        <Button
          variant="contained"
          onClick={() => {
            setMutationError(null);
            setForm(EMPTY_FORM);
          }}
        >
          Add target
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>URL</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell>Companies to mirror</TableCell>
            <TableCell>Batch size</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {targets.map((target) => (
            <TableRow key={target.uuid}>
              <TableCell>{target.name}</TableCell>
              <TableCell>{target.url}</TableCell>
              <TableCell>
                <Chip
                  color={target.enabled ? "success" : "default"}
                  label={target.enabled ? "enabled" : "disabled"}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {target.subscribesToAllCompanies
                  ? "All companies"
                  : `${target.mirroredCompanyCount} companies`}
              </TableCell>
              <TableCell>{target.batchSize}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={() => {
                      startEdit(target);
                    }}
                  >
                    Edit
                  </Button>
                  {!target.subscribesToAllCompanies && (
                    <Button
                      size="small"
                      onClick={() => {
                        setCompaniesTargetUuid(target.uuid);
                      }}
                    >
                      Edit companies
                    </Button>
                  )}
                  <Button
                    color="error"
                    size="small"
                    onClick={() => {
                      void removeTarget(target.uuid);
                    }}
                  >
                    Delete
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
          {!loading && targets.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>No mirror targets configured.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {form !== null && (
        <Box sx={styles.form}>
          <Typography variant="h6">
            {form.uuid === null ? "Add target" : `Edit ${form.name}`}
          </Typography>
          <TextField
            label="Name (lowercase, e.g. staging)"
            size="small"
            value={form.name}
            onChange={(event) => {
              setForm({ ...form, name: event.target.value });
            }}
          />
          <TextField
            label="Base URL"
            size="small"
            value={form.url}
            onChange={(event) => {
              setForm({ ...form, url: event.target.value });
            }}
          />
          <TextField
            label="Batch size per tick"
            size="small"
            value={form.batchSize}
            onChange={(event) => {
              setForm({ ...form, batchSize: event.target.value });
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.enabled}
                onChange={(event) => {
                  setForm({ ...form, enabled: event.target.checked });
                }}
              />
            }
            label="Enabled"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.subscribesToAllCompanies}
                onChange={(event) => {
                  setForm({
                    ...form,
                    subscribesToAllCompanies: event.target.checked,
                  });
                }}
              />
            }
            label="All companies (including future ones)"
          />
          <Stack direction="row" spacing={1}>
            <Button
              disabled={creating || updating}
              variant="contained"
              onClick={() => {
                void submitForm();
              }}
            >
              Save
            </Button>
            <Button
              onClick={() => {
                setForm(null);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
      {companiesTargetUuid !== null && (
        <MirrorTargetCompaniesEditor
          targetUuid={companiesTargetUuid}
          onClose={() => {
            setCompaniesTargetUuid(null);
            void refetch();
          }}
        />
      )}
    </Box>
  );
};

export default MirrorTargetsPanel;
